import { useEffect, useMemo, useState } from 'react';

import {
  mergeAvatarRuntimeConfig,
  type AvatarRuntimeConfig
} from '../avatarRuntimeConfig';

export type AvatarRuntimeConfigLoadStatus = 'idle' | 'loading' | 'ready' | 'error';
export type AvatarRuntimeConfigSource = 'runtime-service' | 'cache' | 'defaults' | 'disabled';

export interface AvatarRuntimeConfigResponse {
  config: Record<string, unknown> | null;
  source?: string;
  fetchedAt?: string;
  updatedAt?: string;
  version?: string | number;
}

export interface AvatarRuntimeConfigResult {
  status: AvatarRuntimeConfigLoadStatus;
  config: AvatarRuntimeConfig | null;
  source: AvatarRuntimeConfigSource | string;
  error: Error | null;
  fetchedAt?: string;
  updatedAt?: string;
  version?: string | number;
}

export interface UseAvatarRuntimeConfigOptions {
  projectToken?: string | null;
  modelId?: string | null;
  runtimeUrl?: string | null;
  enabled?: boolean;
  hostOverrides?: Record<string, unknown> | null;
  ttlMs?: number;
}

interface CacheEntry {
  expiresAt: number;
  response: AvatarRuntimeConfigResponse;
}

const DEFAULT_TTL_MS = 15_000;
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<AvatarRuntimeConfigResponse>>();

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

export function resolveDefaultRuntimeUrl(): string {
  if (typeof window === 'undefined') return '';
  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5075';
  }
  return `${origin.replace(/\/+$/, '')}/runtime-api`;
}

function resolveRuntimeUrl(runtimeUrl?: string | null): string {
  return trimTrailingSlash(runtimeUrl?.trim() || resolveDefaultRuntimeUrl());
}

function buildCacheKey(runtimeUrl: string, projectToken: string, modelId?: string | null): string {
  return `${runtimeUrl}::${projectToken}::${modelId?.trim() || ''}`;
}

function buildAvatarConfigUrl(runtimeUrl: string, projectToken: string, modelId?: string | null): string {
  const url = new URL(`${runtimeUrl}/api/avatar/config/${encodeURIComponent(projectToken)}`);
  const cleanModelId = modelId?.trim();
  if (cleanModelId) {
    url.searchParams.set('modelId', cleanModelId);
  }
  return url.toString();
}

export async function fetchAvatarRuntimeConfig(
  options: Pick<UseAvatarRuntimeConfigOptions, 'projectToken' | 'modelId' | 'runtimeUrl' | 'ttlMs'> & { signal?: AbortSignal } 
): Promise<{ response: AvatarRuntimeConfigResponse; source: AvatarRuntimeConfigSource | string }> {
  const projectToken = options.projectToken?.trim();
  const runtimeUrl = resolveRuntimeUrl(options.runtimeUrl);
  if (!projectToken || !runtimeUrl) {
    return { response: { config: null }, source: 'disabled' };
  }

  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const key = buildCacheKey(runtimeUrl, projectToken, options.modelId);
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return { response: cached.response, source: 'cache' };
  }

  let pending = inflight.get(key);
  if (!pending) {
    pending = fetch(buildAvatarConfigUrl(runtimeUrl, projectToken, options.modelId), {
      headers: { Accept: 'application/json' },
      signal: options.signal
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Avatar config request failed (${response.status})`);
      }
      const body = await response.json() as AvatarRuntimeConfigResponse;
      const normalized: AvatarRuntimeConfigResponse = {
        config: body.config && typeof body.config === 'object' && !Array.isArray(body.config)
          ? body.config as Record<string, unknown>
          : null,
        source: body.source,
        fetchedAt: body.fetchedAt,
        updatedAt: body.updatedAt,
        version: body.version
      };
      cache.set(key, { response: normalized, expiresAt: Date.now() + ttlMs });
      return normalized;
    }).finally(() => {
      inflight.delete(key);
    });
    inflight.set(key, pending);
  }

  return { response: await pending, source: 'runtime-service' };
}

export function useAvatarRuntimeConfig({
  projectToken,
  modelId,
  runtimeUrl,
  enabled = true,
  hostOverrides,
  ttlMs
}: UseAvatarRuntimeConfigOptions): AvatarRuntimeConfigResult {
  const [state, setState] = useState<AvatarRuntimeConfigResult>(() => ({
    status: enabled ? 'loading' : 'idle',
    config: mergeAvatarRuntimeConfig({ modelId, hostOverrides }),
    source: enabled ? 'defaults' : 'disabled',
    error: null
  }));

  const defaults = useMemo(
    () => mergeAvatarRuntimeConfig({ modelId, hostOverrides }),
    [hostOverrides, modelId]
  );

  useEffect(() => {
    const cleanProjectToken = projectToken?.trim();
    if (!enabled || !cleanProjectToken) {
      setState({
        status: 'idle',
        config: defaults,
        source: enabled ? 'defaults' : 'disabled',
        error: null
      });
      return;
    }

    const controller = new AbortController();
    setState((previous) => ({
      ...previous,
      status: previous.config ? 'ready' : 'loading',
      config: previous.config ?? defaults,
      source: previous.config ? previous.source : 'defaults',
      error: null
    }));

    fetchAvatarRuntimeConfig({
      projectToken: cleanProjectToken,
      modelId,
      runtimeUrl,
      ttlMs,
      signal: controller.signal
    })
      .then(({ response, source }) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'ready',
          config: mergeAvatarRuntimeConfig({
            modelId,
            savedConfig: response.config,
            hostOverrides
          }),
          source: source === 'runtime-service' ? (response.source ?? source) : source,
          error: null,
          fetchedAt: response.fetchedAt,
          updatedAt: response.updatedAt,
          version: response.version
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          config: defaults,
          source: 'defaults',
          error: error instanceof Error ? error : new Error(String(error))
        });
      });

    return () => controller.abort();
  }, [defaults, enabled, hostOverrides, modelId, projectToken, runtimeUrl, ttlMs]);

  return state;
}

export default useAvatarRuntimeConfig;
